// src/pages/GrievanceGraph/GrivenaceGraphPage.js

import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import "./GrivenaceGraphPage.css";
import {
  daywiseTrends,
  officerWiseData,
  getDepartments,
  getOfficerTrends,
} from "../../../api/collectorApi";

const getDayWithDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.toLocaleDateString("en-US", { weekday: "short" });
 
  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
  return `${day} (${formattedDate})`;
};

const GrivenaceGraphPage = () => {
  const [baseTrends, setBaseTrends] = useState([]); 
  const [trendsData, setTrendsData] = useState([]); 
  const [loading, setLoading] = useState(true);

  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [loadingDepartments, setLoadingDepartments] = useState(true);

  const [officerList, setOfficerList] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState("");
  const [loadingOfficer, setLoadingOfficer] = useState(true);
 const { t } = useTranslation();
  const departmentChangedByOfficer = useRef(false);

  /* ───────── Load Departments ───────── */
  useEffect(() => {
    async function loadDepartments() {
      try {
        const list = await getDepartments();
        setDepartments(list.map((d) => d.name).filter(Boolean));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingDepartments(false);
      }
    }
    loadDepartments();
  }, []);

  /* ───────── Load Officers ───────── */
  useEffect(() => {
    async function loadOfficers() {
      try {
        const data = await officerWiseData();
        setOfficerList(
          (Array.isArray(data) ? data : []).map((item) => ({
            officerId: item.officerId ?? item.id ?? "",
            name: item.name ?? "Unknown Officer",
            designation: item.designation ?? "",
            department: item.department ?? "",
          }))
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingOfficer(false);
      }
    }
    loadOfficers();
  }, []);

  /* ───────── Load Base Trends  ───────── */
  useEffect(() => {
    async function loadBaseTrends() {
      setLoading(true);
      try {
        const data = await daywiseTrends();
        const formatted = data.map((item) => ({
          date: item.date,
          total: item.totalGrievances,
          departments: item.departments,
        }));
        setBaseTrends(formatted);
        setTrendsData(formatted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadBaseTrends();
  }, []);

  /* ───────── Officer → Department Sync ───────── */
  useEffect(() => {
    if (!selectedOfficer) return;

    const officer = officerList.find(
      (o) => o.officerId === selectedOfficer
    );

    if (officer && officer.department !== selectedDepartment) {
      departmentChangedByOfficer.current = true;
      setSelectedDepartment(officer.department);
    }
  }, [selectedOfficer, officerList]);

  /* ───────── Department Change Logic ───────── */
  useEffect(() => {
    if (departmentChangedByOfficer.current) {
      departmentChangedByOfficer.current = false;
      return;
    }

    setSelectedOfficer("");

    if (selectedDepartment === "ALL") {
      setTrendsData(baseTrends);
    } else {
      setTrendsData(
        baseTrends.map((d) => ({
          date: d.date,
          total: d.departments?.[selectedDepartment]?.total || 0,
        }))
      );
    }
  }, [selectedDepartment, baseTrends]);

  /* ───────── Officer Trends ───────── */
  useEffect(() => {
    if (!selectedOfficer) return;

    async function loadOfficerTrends() {
      // setLoading(true);
      try {
        const officerTrend = await getOfficerTrends(selectedOfficer);
        if (officerTrend?.dailyData) {
          setTrendsData(
            officerTrend.dailyData.map((d) => ({
              date: d.date,
              total: d.total,
            }))
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadOfficerTrends();
  }, [selectedOfficer]);

  /* ───────── Filter Officers ───────── */
  const filteredOfficers =
    selectedDepartment === "ALL"
      ? officerList
      : officerList.filter(
          (o) => o.department === selectedDepartment
        );

  /* ───────── Chart Data ───────── */
  const chartData = trendsData.map((d) => ({
    label: getDayWithDate(d.date),
    complaints: d.total,
  }));

  return (
    <div className="grivenacegraphpage-container">
      <div className="grivenacegraphpage-header">
       <h3 className="grivenacegraphpage-title">
  {t("analytics.title")}
</h3>
      </div>

      {/* Filters */}
      <div
        className="grivenacegraphpage-filterbox"
        style={{
          display: "flex",
          gap: "20px",
          justifyContent: "flex-end",
          marginBottom: "40px",
        }}
      >
        <select
          className="grivenacegraphpage-dropdown"
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          disabled={loadingDepartments}
        >
          <option value="ALL">{t("common.all", "All")}</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>

        <select
          className="grivenacegraphpage-dropdown"
          value={selectedOfficer}
          onChange={(e) => setSelectedOfficer(e.target.value)}
          disabled={loadingOfficer}
        >
         <option value="">
  {t("analytics.selectOfficer")}
</option>
          {filteredOfficers.map((o) => (
            <option key={o.officerId} value={o.officerId}>
              {o.name} ({o.designation})
            </option>
          ))}
        </select>
      </div>

      {/* Chart */}
      <div className="grivenacegraphpage-chartbox">
        {loading ? (
        <p>{t("common.loading")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#cce0ff" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="complaints"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ fill: "#3b82f6", r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

     <p className="grphtxt">
  {t("analytics.trendDescription")}
</p>
    </div>
  );
};

export default GrivenaceGraphPage;
